// Lägg till plats (b5): namn, typ, tidszon med webbläsarens som förval, position, och navet.
// Den som skapar platsen blir ägare. Navets nyckel visas en gång efteråt.

import type { Navsort, Platstyp } from "@batkoll/gemensam";
import { useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import {
  NAVVAL,
  Nyckelruta,
  Positionsval,
  type Positionsvarde,
  Tidszonsval,
  TYPVAL,
  webblasarensTidszon,
} from "./platsform.tsx";

export function Nyplatsvy(props: { skapad: () => Promise<void> }) {
  const [namn, setNamn] = useState("");
  const [typ, setTyp] = useState<Platstyp>("hus");
  const [tidszon, setTidszon] = useState(webblasarensTidszon());
  const [position, setPosition] = useState<Positionsvarde>({ position: null, giltig: true });
  const [nav, setNav] = useState<Navsort>("hus");
  const [fel, setFel] = useState<string | null>(null);
  const [sparar, setSparar] = useState(false);
  const [skapad, setSkapad] = useState<{
    id: string;
    namn: string;
    nyckel: string | null;
    sort: Navsort;
  } | null>(null);
  const sort: Navsort = typ === "bat" ? "ombord" : nav;

  async function spara(e: Event) {
    e.preventDefault();
    setFel(null);
    if (!position.giltig) {
      setFel("Positionen är inte giltig.");
      return;
    }
    setSparar(true);
    try {
      const r = await api.skapaPlats({
        namn,
        typ,
        tidszon,
        position: position.position,
        nav: sort,
      });
      setSkapad({ id: r.plats.id, namn: r.plats.namn, nyckel: r.nyckel, sort: r.sort });
      await props.skapad();
    } catch (x) {
      setFel(x instanceof Felsvar ? x.message : "Platsen gick inte att lägga till.");
    } finally {
      setSparar(false);
    }
  }

  if (skapad) {
    return (
      <div class="rutnat">
        <section class="kort kort-bred" aria-label="Platsen är tillagd">
          <h2>{skapad.namn} är tillagd</h2>
          <p class="meddelande">
            Du är ägare. Fyll i elen under platsens inställningar, så planeras förvärmningen efter
            elpriset.
          </p>
        </section>
        {skapad.nyckel && (
          <Nyckelruta
            nyckel={skapad.nyckel}
            sort={skapad.sort}
            klar={() => setSkapad({ ...skapad, nyckel: null })}
          />
        )}
        {/* b15: guiden startas när platsen är skapad, efter nyckeln. */}
        <div class="knapprad kort-bred">
          <a class="knapp-huvud knapplank" href={`#plats/${skapad.id}/kom-igang`}>
            Kom igång: starta guiden
          </a>
          <a class="knapp-andra knapplank" href={`#plats/${skapad.id}/installningar`}>
            Till platsens inställningar
          </a>
          <a class="knapp-andra knapplank" href="#start">
            Till översikten
          </a>
        </div>
      </div>
    );
  }

  return (
    <section class="kort resa formular" aria-label="Lägg till plats">
      <h2>Lägg till plats</h2>
      <form onSubmit={spara}>
        <label>
          Namn
          <input
            required
            maxLength={60}
            autoComplete="off"
            value={namn}
            onInput={(e) => setNamn(e.currentTarget.value)}
          />
        </label>
        <label>
          Typ
          <select value={typ} onChange={(e) => setTyp(e.currentTarget.value as Platstyp)}>
            {TYPVAL.map((t) => (
              <option key={t.typ} value={t.typ}>
                {t.namn}
              </option>
            ))}
          </select>
        </label>
        <Tidszonsval varde={tidszon} andra={setTidszon} />
        <Positionsval start={null} andra={setPosition} />
        {typ === "bat" ? (
          <p class="liten">Båten får ett nav ombord. Båten bara läses, och inget styrs.</p>
        ) : (
          <fieldset class="platsval">
            <legend>Navet</legend>
            {(["hus", "ha"] as const).map((s) => (
              <label key={s} class="kryss">
                <input type="radio" name="nav" checked={nav === s} onChange={() => setNav(s)} />
                {NAVVAL[s]}
              </label>
            ))}
          </fieldset>
        )}
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit" disabled={sparar}>
            Lägg till platsen
          </button>
          <a class="knapp-andra knapplank" href="#start">
            Avbryt
          </a>
        </div>
      </form>
    </section>
  );
}
